import React, {Fragment, useEffect, useState} from "react";
import {Garage, Image, PartnerConfig} from "@App/types";
import "leaflet/dist/leaflet.css"
import GarageListItemSlider from "@App/components/GarageList/GarageListParts/GarageListItemParts/GarageListItemSlider";
import GarageListItemBrandIcons from "@App/components/GarageList/GarageListParts/GarageListItemParts/GarageListItemBrandIcons";
import {format, parseISO} from "date-fns";
import {de} from "date-fns/locale";
import {Text} from "@eo-locale/react";
import {useRouter} from "next/router";
import {useDaysLeft} from "@App/hooks/useDaysLeft";


export type GarageListPopupProps = {
    garage: Garage
    partner: PartnerConfig
};

const formatShortDate = (date: Date | string): string => {
    if (typeof date === 'string') {
        date = parseISO(date)
    }
    return format(date, 'dd.MM.', {locale: de})
}


const GarageListPopup = React.memo<GarageListPopupProps>(({garage, partner}: GarageListPopupProps) => {

    const router = useRouter()

    const [availableDay, setAvailableDay] = useState<Date | null>(null)

    const {getDaysLeft, getFormattedText} = useDaysLeft()

    useEffect(() => {
        const listItem = document.getElementById(`garageListItemCard-${garage.id}`);
        const list = document.getElementById(`garageItemList`);
        if (listItem && list && window.innerWidth > 767) {
            const listItemTop = listItem.offsetTop - 250;
            list.scroll({
                top: listItemTop,
                behavior: "smooth"
            })
        }
        garage.available_days && garage.available_days.length > 0 && setAvailableDay(garage.available_days[0].available_at)
    }, [garage.available_days])


    const handleNavigate = () => {
        router.push(`/${partner.slug}/${garage.profileSlug?.split('/')[0]}/${garage.id}-${garage.profileSlug?.split('/')[1]}/`)
    }

    const images: Image[] = [
        {sizes: garage.cover_image_sizes},
        ...garage.images
    ]

    const rating = garage.rating ? (garage.rating * 0.05).toFixed(2) : '';

    return (
        <div className="grid grid-cols-12 relative pb-4">

            {(garage.profileSlug && availableDay && garage.pivot?.bookable) && (
                <span className={'garageListItemCard__link'}
                      onClick={handleNavigate}></span>
            )}

            <div className={"col-span-12 pb-4"}>
                <GarageListItemSlider images={images} alt={garage.name} id={garage.id} partner={partner}/>
            </div>

            <div className={"px-4 flex col-span-12 items-center pb-3 headlineContainer"}>
                <div className={'text-base leading-6 font-semibold text-gray-900 flex-grow pr-2'}>
                    {garage.name.length > 20 ? garage.name.substring(0, 17) + '...' : garage.name}
                </div>

                {rating !== '' && (
                    <span className={'text-sm leading-5 font-semibold flex-shrink-0'}>
                        <i className={`fa-kit fa-star fa-fw text-yellow-500 text-regular relative inline-block mr-1`}/>{rating}
                        <span className={'font-normal'}> ({garage.review_count?.toString()})</span>
                    </span>
                )}
            </div>

            {garage.garageConcept && (
                <div className={'col-span-full flex items-center gap-x-2 text-sm px-4 my-2'}>
                    <img loading={'lazy'}
                         className={'h-6'}
                         src={process.env.NEXT_PUBLIC_IMG_SRC + garage.garageConcept.logo}
                         alt={garage.garageConcept.name}
                    />{garage.garageConcept.name}
                </div>
            )}
            {!garage.garageConcept && garage.authorizedBrands && garage.authorizedBrands.length > 0 && (
                <GarageListItemBrandIcons brands={garage.authorizedBrands}/>
            )}
            {!garage.garageConcept && garage.authorizedBrands && garage.authorizedBrands.length === 0 && (
                <div className={'garageListItemCard__right__authorizedBrands flex items-center'}>
                    <i className={'fa-kit fa-garage fa-fw text-gray-700 mr-2 text-xl float-left inline-block align-middle'}/>
                    <span className={'garageListItemCard__right__authorizedBrands__garageText'}><Text id={'free_garage'}/></span>
                </div>
            )}
            {(!availableDay || !garage.pivot?.bookable) && partner.shouldDisplayPhoneIfUnavailable ? (
                <Fragment>
                    {((garage.email || garage.website) && partner.shouldDisplayHomepageAndMailIfNotBookable) ? (
                        <Fragment>
                            <div className={'flex items-center col-span-full md:mx-6 text-base leading-6 pt-4'}>
                                {garage.website && (
                                    <a className={'flex-grow garageListItem__brandIcons__garageText no-underline'}
                                       href={`${garage.website}`} target={'_blank'} rel={'nofollow noreferrer'}>
                                        <i className={`fa-kit fa-globe fa-fw text-base inline-block align-middle pr-3`}/> Website
                                    </a>
                                )}
                                {garage.email && (
                                    <a className={'flex-grow garageListItem__brandIcons__garageText no-underline'}
                                       href={`mailto:${garage.email}`}>
                                        <i className={`fa-kit fa-envelope fa-fw text-base inline-block align-middle pr-3`}/> Email
                                    </a>
                                )}
                                <span className={'flex-grow garageListItem__brandIcons__garageText'}>
                                    <i className={`fa-kit fa-phone fa-fw text-primary text-base inline-block align-middle pr-3`}/>
                                    <a href={`tel:${garage.phone}`}>{garage.phone}</a>
                                </span>
                            </div>
                        </Fragment>
                        ) :
                        <Fragment>
                            <div className={'flex items-center col-span-full md:mx-6 text-base leading-6 pt-4'}>
                                <span className={'flex-grow garageListItem__brandIcons__garageText'}>
                                    <i className={`fa-kit fa-phone fa-fw text-primary text-base inline-block align-middle pr-3`}/>
                                    <a href={`tel:${garage.phone}`}>{garage.phone}</a>
                                </span>
                            </div>
                        </Fragment>
                    }

                </Fragment>

                ) :
                <div className={'col-span-12 flex px-4 text-base leading-6 font-semibold'}>
                    {garage.pivot?.bookable && (
                        <span>
                            {availableDay ? (
                                    <>
                                        {formatShortDate(garage.available_days[0].available_at)}&nbsp;
                                        <Text
                                            id={getFormattedText(getDaysLeft(availableDay))}
                                            days={getDaysLeft(availableDay)}
                                        />
                                    </>
                                ) : 'n/a'
                            }
                        </span>
                    )}
                    {partner.shouldDisplayPrize && (
                        <span className={'flex-grow text-right'}>
                            € {garage.calculation && garage.calculation.estimated_price > 0 ? garage.calculation.estimated_price.toFixed(2) : 'vor Ort'}
                        </span>
                    )}
                </div>
            }
        </div>
    );
})

GarageListPopup.displayName = "GarageListPopup";
export default GarageListPopup
